// TopBar account menu - the signed-in user's avatar opens a panel with
// identity, a Profile link, and Sign out.
//
// Sign out is client-side by design: the server keeps no session state
// (7-day JWT, no revocation list), so dropping the token and returning
// to /login is the complete flow. Other tabs notice via the storage
// event that TopBar's useHasToken already listens to.

import { Link, useNavigate } from "react-router-dom";
import { LogOut, UserRound } from "lucide-react";
import { clearToken, initialsOf } from "../../api/auth";
import { useCurrentUser } from "../auth/CurrentUserContext";
import { useDismissable } from "../../hooks/useDismissable";

export function AccountMenu() {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const { open, setOpen, containerRef } = useDismissable<HTMLDivElement>();

  const signOut = () => {
    clearToken();
    navigate("/login");
  };

  const initials = user ? initialsOf(user.fullName) : "?";

  return (
    <div ref={containerRef} className="cc-account">
      <button
        type="button"
        className="cc-user-av cc-account-btn"
        aria-label="Account"
        aria-haspopup="menu"
        aria-expanded={open}
        title={user ? `${user.fullName} (${user.email})` : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {initials}
      </button>

      {open && (
        <div className="cc-account-panel cc-card" role="menu" aria-label="Account">
          <div className="cc-account-head">
            <span className="cc-user-av" aria-hidden>
              {initials}
            </span>
            <span className="cc-account-id">
              <span className="name">{user?.fullName ?? "Signed in"}</span>
              {user && <span className="mail">{user.email}</span>}
              {user && <span className="role">{user.role}</span>}
            </span>
          </div>

          <div className="cc-account-list">
            <Link to="/profile" className="cc-account-row" role="menuitem">
              <UserRound size={14} strokeWidth={2} aria-hidden />
              <span>Profile</span>
            </Link>
            <button type="button" className="cc-account-row" role="menuitem" onClick={signOut}>
              <LogOut size={14} strokeWidth={2} aria-hidden />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
